import React, { useCallback, useEffect, useId, useState } from 'react';
import { bookingApi } from '../utils/bookingApi';
import { Tabs, Picker, InlineError } from './ui';
import '../pages/booking/booking-shared.css';
import './RecipientPicker.css';

const STANDARD_PRESETS = [
  { label: 'All members', filter: { type: 'all' } },
  { label: 'Adults only', filter: { type: 'role', role: 'ADULT' } },
  { label: 'Coaches only', filter: { type: 'role', role: 'COACH' } },
  { label: 'Active membership', filter: { type: 'active_membership' } },
  { label: 'Overdue subscriptions', filter: { type: 'pending_payment_membership' } },
  { label: 'Expiring credits', filter: { type: 'expiring_credits' } },
  { label: 'No upcoming bookings', filter: { type: 'no_upcoming_bookings' } },
  { label: 'Attended in the last 3 months', filter: { type: 'attended_recently', withinDays: 90 } },
];

// Who an email goes to: a standard list, a saved group, or people picked by
// hand. The three ways are tabs; the open one isn't kept in the address, as
// this sits inside other pages and pop-ups that have their own.
export default function RecipientPicker({ value, onChange, groups = [], forceEnabled = false }) {
  const enabled = forceEnabled || (value !== null && value !== undefined);
  const ids = useId();
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [mode, setMode] = useState('preset');
  const [adhocUsers, setAdhocUsers] = useState(null);
  const [usersFailed, setUsersFailed] = useState(false);
  const [adhocSelected, setAdhocSelected] = useState(
    value?.type === 'adhoc' ? (value.userIds || []) : []
  );

  const loadUsers = useCallback(() => {
    setUsersFailed(false);
    const API_URL = `${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api`;
    const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
    fetch(`${API_URL}/users`, { headers })
      .then(r => r.json())
      .then(data => setAdhocUsers(Array.isArray(data) ? data : data.users || []))
      .catch(() => setUsersFailed(true));
  }, []);

  useEffect(() => {
    if (mode === 'adhoc' && adhocUsers === null) loadUsers();
  }, [mode, adhocUsers, loadUsers]);

  const handleToggle = () => {
    if (enabled) {
      onChange(null);
      setPreview(null);
    } else {
      onChange(STANDARD_PRESETS[0].filter);
      setPreview(null);
    }
  };

  const handlePresetChange = (filter) => {
    onChange(filter);
    setPreview(null);
  };

  const handleGroupChange = (groupId) => {
    const group = groups.find(g => g.id === groupId);
    if (group) {
      onChange(group.recipientFilter);
      setPreview(null);
    }
  };

  const handleAdhocChange = (next) => {
    setAdhocSelected(next);
    onChange(next.length > 0 ? { type: 'adhoc', userIds: next } : null);
    setPreview(null);
  };

  const handlePreview = async () => {
    if (!value) return;
    setPreviewing(true);
    setPreview(null);
    try {
      const res = await bookingApi.previewNoticeboardRecipients(value);
      setPreview(res.data);
    } catch {
      setPreview({ failed: true });
    } finally {
      setPreviewing(false);
    }
  };

  const tabs = [
    { id: 'preset', label: 'Standard' },
    groups.length > 0 && { id: 'group', label: 'Saved group' },
    { id: 'adhoc', label: 'Pick people' },
  ].filter(Boolean);

  const people = (adhocUsers || []).map(u => ({
    id: u.id,
    label: `${u.firstName} ${u.lastName}`,
    detail: u.email,
    search: u.email,
  }));

  return (
    <div className="recipient-picker">
      {!forceEnabled && (
        <label className="recipient-picker__notify">
          <input type="checkbox" className="bk-checkbox" checked={enabled} onChange={handleToggle} />
          <span>Notify by email</span>
        </label>
      )}

      {enabled && (
        <div className="recipient-picker__box">
          <Tabs tabs={tabs} value={mode} onChange={(m) => { setMode(m); setPreview(null); }} label="Choose recipients by" />

          {mode === 'preset' && (
            <label className="bk-label">
              Send to
              <select
                className="bk-input"
                value={JSON.stringify(value)}
                onChange={e => handlePresetChange(JSON.parse(e.target.value))}
              >
                {STANDARD_PRESETS.map(p => (
                  <option key={p.label} value={JSON.stringify(p.filter)}>{p.label}</option>
                ))}
              </select>
            </label>
          )}

          {mode === 'group' && (
            <label className="bk-label">
              Saved group
              <select
                className="bk-input"
                onChange={e => handleGroupChange(e.target.value)}
                defaultValue=""
              >
                <option value="" disabled>Choose a group…</option>
                {groups.map(g => (
                  <option key={g.id} value={g.id}>{g.name}</option>
                ))}
              </select>
            </label>
          )}

          {mode === 'adhoc' && (
            <div className="bk-label">
              <label htmlFor={`${ids}-people`}>People</label>
              {usersFailed ? (
                <InlineError onRetry={loadUsers}>Couldn't load the members list.</InlineError>
              ) : (
                <Picker
                  id={`${ids}-people`}
                  label="People"
                  multiple
                  items={people}
                  value={adhocSelected}
                  onChange={handleAdhocChange}
                  placeholder="Search by name or email…"
                  disabled={adhocUsers === null}
                />
              )}
              {adhocSelected.length > 0 && (
                <span className="bk-small">{adhocSelected.length} chosen</span>
              )}
            </div>
          )}

          <div className="recipient-picker__preview">
            <button
              type="button"
              className="bk-btn"
              onClick={handlePreview}
              disabled={previewing || !value}
            >
              {previewing ? 'Checking…' : 'Preview recipients'}
            </button>
            {preview && (
              <span className="bk-small" role={preview.failed ? 'alert' : undefined}>
                {preview.failed
                  ? "Couldn't work out who it goes to. Try again."
                  : preview.count === 0
                    ? 'No recipients found'
                    : `${preview.count} recipient${preview.count !== 1 ? 's' : ''}${preview.preview.length > 0 ? `: ${preview.preview.map(r => r.name).join(', ')}${preview.count > 5 ? '…' : ''}` : ''}`}
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
